import * as React from 'react';
import { getCommonProps } from '../helpers/common-props';
import { styled } from '../theme-provider';
const StyledFadeWrapper = styled.div.withConfig({ displayName: "StyledFadeWrapper", componentId: "sc-4rg3xe" }) `opacity:${(props) => props.animatedOpacity};transform:${(props) => props.animatedTransform};`;
StyledFadeWrapper.displayName = 'StyledFadeWrapper';
const DEFAULT_TRANSLATION = 'translate(0,0)';
export const getTransform = ({ directionFrom, distance, }) => {
    if (!directionFrom || !distance) {
        return {
            startTransform: DEFAULT_TRANSLATION,
            endTransform: DEFAULT_TRANSLATION,
        };
    }
    const xAxisSign = directionFrom === 'left' ? -1 : 1;
    const yAxisSign = directionFrom === 'top' ? -1 : 1;
    const xAxis = directionFrom === 'left' || directionFrom === 'right' ? distance : '0px';
    const yAxis = directionFrom === 'top' || directionFrom === 'bottom' ? distance : '0px';
    return {
        startTransform: `translate(calc(${xAxisSign} * ${xAxis}), calc(${yAxisSign} * ${yAxis}))`,
        endTransform: DEFAULT_TRANSLATION,
    };
};
export const getOpacity = (fadeMode) => {
    switch (fadeMode) {
        case 'disable':
            return {
                startOpacity: 1,
                endOpacity: 1,
            };
        case 'invert':
            return {
                startOpacity: 1,
                endOpacity: 0,
            };
        default:
            return {
                startOpacity: 0,
                endOpacity: 1,
            };
    }
};
export const handleIntersectionObserver = (entries, setter) => {
    const [entry] = entries;
    if (entry.isIntersecting) {
        setter((prevState) => (prevState === 'init' ? 'running' : prevState));
    }
};
export const Fade = (props) => {
    const { children, delay = 0, duration = 400, easing = 'linear', disableAnimation = false, directionFrom, distance, fraction = 0, fadeMode = 'normal', onReveal, } = props;
    const [animationState, setAnimationState] = React.useState(disableAnimation ? 'none' : 'init');
    const commonProps = getCommonProps(props);
    const fadeRef = React.useRef(null);
    const { startTransform, endTransform } = getTransform({
        directionFrom,
        distance,
    });
    const { startOpacity, endOpacity } = getOpacity(fadeMode);
    const getAnimationKeyframes = React.useCallback(() => {
        return [
            {
                opacity: startOpacity,
                transform: startTransform,
            },
            {
                opacity: endOpacity,
                transform: endTransform,
            },
        ];
    }, [startOpacity, startTransform, endOpacity, endTransform]);
    const getAnimationOptions = React.useCallback(() => {
        return {
            delay,
            duration,
            easing,
        };
    }, [delay, duration, easing]);
    React.useEffect(() => {
        if (!fadeRef.current || animationState !== 'running') {
            return;
        }
        const animation = fadeRef.current.animate(getAnimationKeyframes(), getAnimationOptions());
        animation.addEventListener('finish', () => {
            setAnimationState('finished');
            onReveal === null || onReveal === void 0 ? void 0 : onReveal();
        });
        return () => {
            setAnimationState('finished');
            animation.removeEventListener('finish', () => {
                setAnimationState('finished');
                onReveal === null || onReveal === void 0 ? void 0 : onReveal();
            });
        };
    }, [animationState, getAnimationOptions, getAnimationKeyframes, onReveal]);
    React.useEffect(() => {
        if (!fadeRef.current || animationState !== 'init') {
            return;
        }
        const intersectionObserverOptions = {
            threshold: fraction,
        };
        const io = new IntersectionObserver((entries) => handleIntersectionObserver(entries, setAnimationState), intersectionObserverOptions);
        io.observe(fadeRef.current);
        return () => {
            io.disconnect();
        };
    }, [animationState, fraction]);
    React.useEffect(() => {
        setAnimationState(disableAnimation ? 'none' : 'init');
    }, [disableAnimation]);
    const animatedOpacity = animationState !== 'finished' ? startOpacity : endOpacity;
    const animatedTransform = animationState !== 'finished' ? startTransform : endTransform;
    return animationState === 'none' ? (React.createElement(React.Fragment, null, children)) : (React.createElement(StyledFadeWrapper, Object.assign({ ref: fadeRef, animatedOpacity: animatedOpacity, animatedTransform: animatedTransform }, commonProps), children));
};
